import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activePallets: 0,
    activeWheels: 0,
    todayShipments: 0,
    totalItems: 0
  });
  const [recentPallets, setRecentPallets] = useState([]);
  const [recentWheels, setRecentWheels] = useState([]);
  const [recentShipments, setRecentShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createMenuOpen && !event.target.closest('.create-menu-container')) {
        setCreateMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [createMenuOpen]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [palletsRes, wheelsRes, shipmentsRes, itemsRes] = await Promise.all([
        api.get('/pallets'),
        api.get('/wheels'),
        api.get('/shipments'),
        api.get('/items')
      ]);

      const pallets = palletsRes.data || [];
      const wheels = wheelsRes.data || [];
      const shipments = shipmentsRes.data || [];
      const items = itemsRes.data || [];

      // Calculate stats
      const activePallets = pallets.filter(p => p.status === 'active').length;
      const activeWheels = wheels.length;

      // Today's shipments
      const today = new Date().toISOString().split('T')[0];
      const todayShipments = shipments.filter(s => {
        const shipDate = new Date(s.created_at).toISOString().split('T')[0];
        return shipDate === today;
      }).length;

      setStats({
        activePallets,
        activeWheels,
        todayShipments,
        totalItems: items.length
      });

      // Get 5 most recent pallets
      const sortedPallets = [...pallets]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentPallets(sortedPallets);

      // Get 5 most recent wheels
      const sortedWheels = [...wheels]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentWheels(sortedWheels);

      // Get 5 most recent shipments
      const sortedShipments = [...shipments]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentShipments(sortedShipments);

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-top">
        <div className="dashboard-header">
          <h1>Welcome, {user?.username || 'User'}</h1>
          <p className="dashboard-subtitle">Welcome back! Here's your overview.</p>
        </div>

        <div className="quick-actions">
          <div className="create-menu-container">
            <button
              className="action-button primary"
              onClick={() => setCreateMenuOpen(!createMenuOpen)}
            >
              <span className="button-icon">+</span>
              <span className="button-text">New</span>
              <span className="dropdown-arrow">{createMenuOpen ? '▲' : '▼'}</span>
            </button>
            {createMenuOpen && (
              <div className="create-dropdown">
                <button
                  className="dropdown-item"
                  onClick={() => window.location.href = '/wheels'}
                >
                  <span className="dropdown-icon">🛒</span>
                  New Wheel
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => window.location.href = '/pallets'}
                >
                  <span className="dropdown-icon">📦</span>
                  New Pallet
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => window.location.href = '/shipments'}
                >
                  <span className="dropdown-icon">🚚</span>
                  New Shipment
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => window.location.href = '/customers'}
                >
                  <span className="dropdown-icon">👤</span>
                  New Customer
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => window.location.href = '/settings?tab=carriers'}
                >
                  <span className="dropdown-icon">🚛</span>
                  New Carrier
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card clickable" onClick={() => window.location.href = '/pallets?status=active'}>
          <div className="stat-icon" style={{ backgroundColor: '#8B1C24' }}>📦</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activePallets}</div>
            <div className="stat-label">Active Pallets</div>
          </div>
        </div>

        <div className="stat-card clickable" onClick={() => window.location.href = '/shipments'}>
          <div className="stat-icon" style={{ backgroundColor: '#2C7A7B' }}>🚚</div>
          <div className="stat-content">
            <div className="stat-value">{stats.todayShipments}</div>
            <div className="stat-label">Today's Shipments</div>
          </div>
        </div>

        <div className="stat-card clickable" onClick={() => window.location.href = '/items'}>
          <div className="stat-icon" style={{ backgroundColor: '#6B161C' }}>🍞</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalItems}</div>
            <div className="stat-label">Total Items</div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Wheels</h2>
          </div>
          <div className="section-content">
            {recentWheels.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🛒</div>
                <p className="empty-text">No wheels created yet</p>
                <p className="empty-subtext">Create a wheel to start organizing items</p>
              </div>
            ) : (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Wheel #</th>
                      <th>Items</th>
                      <th>Weight</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentWheels.map((wheel) => (
                      <tr key={wheel.id} onClick={() => window.location.href = `/wheels?id=${wheel.id}`}>
                        <td className="wheel-number">{wheel.wheel_number}</td>
                        <td>{wheel.item_count || 0}</td>
                        <td>{parseFloat(wheel.total_weight || 0).toFixed(1)} kg</td>
                        <td>{formatDate(wheel.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="section-footer">
                  <button className="view-all-button" onClick={() => window.location.href = '/wheels'}>
                    View All Wheels →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Pallets</h2>
          </div>
          <div className="section-content">
            {recentPallets.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📦</div>
                <p className="empty-text">No pallets created yet</p>
                <p className="empty-subtext">Start by creating your first pallet</p>
              </div>
            ) : (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Pallet #</th>
                      <th>Items</th>
                      <th>Weight</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPallets.map((pallet) => (
                      <tr key={pallet.id} onClick={() => window.location.href = `/pallets?id=${pallet.id}`}>
                        <td className="pallet-number">{pallet.pallet_number}</td>
                        <td>{pallet.item_count || 0}</td>
                        <td>{parseFloat(pallet.total_weight || 0).toFixed(1)} kg</td>
                        <td>{formatDate(pallet.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="section-footer">
                  <button className="view-all-button" onClick={() => window.location.href = '/pallets'}>
                    View All Pallets →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Shipments</h2>
          </div>
          <div className="section-content">
            {recentShipments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🚚</div>
                <p className="empty-text">No shipments yet</p>
                <p className="empty-subtext">Create a shipment when ready</p>
              </div>
            ) : (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Shipment #</th>
                      <th>Customer</th>
                      <th>Pallets</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentShipments.map((shipment) => (
                      <tr key={shipment.id} onClick={() => window.location.href = `/shipments?id=${shipment.id}`}>
                        <td className="shipment-number">{shipment.shipment_number}</td>
                        <td>{shipment.customer_name}</td>
                        <td>{shipment.pallet_count || 0}</td>
                        <td>{formatDate(shipment.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="section-footer">
                  <button className="view-all-button" onClick={() => window.location.href = '/shipments'}>
                    View All Shipments →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
